import React, { useState, useEffect, useRef } from 'react';
import { API } from './config';
import NeedForm from './components/NeedForm';
import NeedList from './components/NeedList';
import AlumniList from './components/AlumniList';
import RequestsList from './components/RequestsList';
import ClassBoard from './components/ClassBoard';
import WelcomePage from './components/WelcomePage';
import LandingPage from './components/LandingPage';
import RegisterForm from './components/RegisterForm';
import ClassConfirmations from './components/ClassConfirmations';

const TABS = [
  { key: 'publish', label: '发动态' },
  { key: 'needs', label: '校友圈' },
  { key: 'board', label: '班级留言板' },
  { key: 'requests', label: '请求' },
  { key: 'alumni', label: '校友墙' },
  { key: 'register', label: '加入校友会' },
];

function App() {
  const [tab, setTab] = useState('needs');
  const [needs, setNeeds] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const [classmates, setClassmates] = useState([]);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeUser, setWelcomeUser] = useState(null);
  const wsRef = useRef(null);

  function refreshData() {
    fetch(API.needs).then(r => r.json()).then(setNeeds).catch(() => {});
    fetch(API.users).then(r => r.json()).then(data => {
      setUsers(data);
      if (currentUser) {
        const updated = data.find(u => u.id === currentUser.id);
        if (updated) setCurrentUser(updated);
      }
    }).catch(() => {});
    if (currentUser) {
      fetch(`${API.classmates}/${currentUser.id}`).then(r => r.json()).then(setClassmates).catch(() => {});
    }
  }

  useEffect(() => {
    fetch(API.categories).then(r => r.json()).then(setCategories).catch(() => {});
    refreshData();
    const pollTimer = setInterval(refreshData, 10000);
    return () => clearInterval(pollTimer);
  }, [currentUser]);

  useEffect(() => {
    let mounted = true;
    let retryTimer = null;
    function connectWs() {
      try {
        const ws = new WebSocket(API.ws);
        ws.onopen = () => { if (mounted) setWsConnected(true); };
        ws.onmessage = (e) => {
          if (!mounted) return;
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'auto_event' || data.type === 'auto_matched') {
              setEvents(prev => [{ text: data.text, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 50));
              refreshData();
            }
            if (data.type === 'new_need' || data.type === 'need_connected') {
              refreshData();
            }
          } catch {}
        };
        ws.onclose = () => { if (mounted) { setWsConnected(false); retryTimer = setTimeout(connectWs, 5000); } };
        ws.onerror = () => { if (mounted) { setWsConnected(false); } };
        wsRef.current = ws;
      } catch {
        if (mounted) retryTimer = setTimeout(connectWs, 10000);
      }
    }
    connectWs();
    return () => { mounted = false; if (wsRef.current) wsRef.current.close(); if (retryTimer) clearTimeout(retryTimer); };
  }, []);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      {currentUser && (
        <div style={{ maxWidth: 430, margin: '0 auto', padding: '10px 14px', borderBottom: '1px solid #eee', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ margin: 0, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              🏫 校友会 AI
            </div>
            <span style={{ fontSize: 13, color: '#555' }}>{currentUser.name}
              {currentUser.classVerified && <span style={{ fontSize: 10, color: '#4caf50', marginLeft: 4 }}>✓已认证</span>}
              {!currentUser.classVerified && currentUser.className && <span style={{ fontSize: 10, color: '#ff9800', marginLeft: 4 }}>未认证</span>}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
            {TABS.filter(t => t.key !== 'register').map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{ ...tabBtnStyle, background: tab === t.key ? '#e91e63' : '#f5f5f5', color: tab === t.key ? '#fff' : '#666' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!currentUser ? (
        <LandingPage
          users={users}
          needs={needs}
          onRegister={(user, sameClass) => {
            setUsers(prev => [...prev, user]);
            setWelcomeUser({ ...user, classCount: sameClass || 0 });
            setShowWelcome(true);
          }}
          onLogin={user => { if (user) { setCurrentUser(user); setTab('board'); } }}
        />
      ) : (
        <div style={{ maxWidth: 430, margin: '0 auto', padding: '0 14px' }}>
          <ClassConfirmations currentUser={currentUser} onRefresh={refreshData} />
          {tab === 'publish' && <NeedForm categories={categories} currentUser={currentUser} />}
          {tab === 'needs' && <NeedList needs={needs} currentUser={currentUser} onRefresh={refreshData} />}
          {tab === 'board' && <ClassBoard currentUser={currentUser} />}
          {tab === 'requests' && <RequestsList currentUser={currentUser} onRefresh={refreshData} />}
          {tab === 'alumni' && <AlumniList users={users} currentUser={currentUser} classmates={classmates} />}
          {tab === 'register' && (
            <RegisterForm onRegister={user => { setCurrentUser(user); setUsers(prev => [...prev, user]); setTab('alumni'); }} />
          )}
        </div>
      )}

      {currentUser && events.length > 0 && (
        <div style={{ marginTop: 16, padding: 8, background: '#fce4ec', borderRadius: 8, fontSize: 13, color: '#666', maxHeight: 120, overflowY: 'auto' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12, color: '#e91e63' }}>🔄 校友动态</div>
          {events.map((e, i) => (
            <div key={i} style={{ padding: '2px 0' }}><span style={{ color: '#999' }}>{e.time}</span> {e.text}</div>
          ))}
        </div>
      )}

      {showWelcome && welcomeUser && (
        <WelcomePage
          user={welcomeUser}
          classCount={welcomeUser.classCount || 0}
          onDone={() => { setShowWelcome(false); setCurrentUser(welcomeUser); setTab('board'); }}
        />
      )}
    </div>
  );
}

const inputStyle = { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 };
const btnStyle = { padding: '6px 16px', border: 'none', borderRadius: 6, background: '#e91e63', color: '#fff', cursor: 'pointer', fontSize: 14 };
const tabBtnStyle = { padding: '6px 16px', border: 'none', borderRadius: 16, cursor: 'pointer', fontSize: 13, fontWeight: 500 };

export default App;
